import React from 'react';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Link,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';

interface GuestInfo {
  name: string;
  email: string;
  phone: string;
}

interface GuestInfoFormProps {
  guestInfo: GuestInfo;
  onGuestInfoChange: (info: GuestInfo) => void;
  onSubmit: (info: GuestInfo) => Promise<void>;
  onBack?: () => void;
  loading: boolean;
}

const GuestInfoForm: React.FC<GuestInfoFormProps> = ({
  guestInfo,
  onGuestInfoChange,
  onSubmit,
  onBack,
  loading,
}) => {
  const { t } = useLanguage();
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Pass the current guestInfo to onSubmit
    await onSubmit(guestInfo);
  };

  return (
    <Box sx={{ width: '100%' }}>
      {onBack && (
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={onBack} 
            sx={{ mr: 2 }}
            aria-label={t('button.back')}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">{t('guestInfo.title')}</Typography>
        </Box>
      )}
      
      <Paper sx={{ p: 3 }}>
        {!onBack && (
          <Typography variant="h6" gutterBottom>
            {t('guestInfo.title')}
          </Typography>
        )}
        
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('guestInfo.name')}
                name="name"
                value={guestInfo.name}
                onChange={(e) => onGuestInfoChange({ ...guestInfo, name: e.target.value })}
                required
                sx={{
                  '& label': { color: 'text.secondary' },
                  '& label.Mui-focused': { color: 'primary.main' },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('guestInfo.email')}
                name="email"
                type="email"
                value={guestInfo.email}
                onChange={(e) => onGuestInfoChange({ ...guestInfo, email: e.target.value })}
                required
                sx={{
                  '& label': { color: 'text.secondary' },
                  '& label.Mui-focused': { color: 'primary.main' },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('guestInfo.phone')}
                name="phone"
                value={guestInfo.phone}
                onChange={(e) => onGuestInfoChange({ ...guestInfo, phone: e.target.value })}
                required
                sx={{
                  '& label': { color: 'text.secondary' },
                  '& label.Mui-focused': { color: 'primary.main' },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {onBack && (
                  <Button
                    variant="outlined"
                    onClick={onBack}
                    sx={{ mr: 2 }}
                  >
                    {t('button.back')}
                  </Button>
                )}
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  fullWidth={!onBack}
                >
                  {loading ? t('confirm.processing') : t('guestInfo.submit')}
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" align="center">
                {t('guestInfo.createAccount')}
                <Link component={RouterLink} to="/register" sx={{ ml: 1 }}>
                  {t('auth.register')}
                </Link>
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default GuestInfoForm; 